/**
 * ProvisionTimingSummaryBars — the non-superadmin view of a provision's timing.
 *
 * Two bars: how long the sandbox took to become **ready** (booted, repo on the
 * target branch) and how long it took to **build** (images built, app healthy).
 * Everything finer than that names our own infrastructure, so the API doesn't
 * send it here — see `provision-timing-view.ts`. This component therefore
 * renders what it is given; there is no filtered step list to fall back to.
 *
 * @module features/sandboxes/components/overview/ProvisionTimingSummaryBars
 */
import React from 'react';
import type { ProvisionTimingSummary } from '@chatsdet/types';
import { formatDurationLabel } from '@chatsdet/ui';
import { provisionPhaseGroups } from './provisionTimingMeta';

export interface ProvisionTimingSummaryBarsProps {
  summary: ProvisionTimingSummary;
}

export function ProvisionTimingSummaryBars({ summary }: ProvisionTimingSummaryBarsProps) {
  // Scale against the summed halves rather than `totalMs`: the total also covers
  // the gaps between phases, so scaling by it would leave a permanent unexplained
  // sliver of empty track at the end of both bars.
  const scaleMs = Math.max(summary.readyMs + summary.buildMs, 1);

  const rows = provisionPhaseGroups.map((group) => ({
    id: group.id,
    label: group.label,
    description: group.description,
    durationMs: group.id === 'ready' ? summary.readyMs : summary.buildMs,
  }));

  return (
    <div className="flex flex-col gap-2">
      {rows.map((row) => (
        <div key={row.id} className="flex items-center gap-2">
          <span className="text-xs w-12 shrink-0 text-muted-foreground" title={row.description}>
            {row.label}
          </span>
          <div className="flex-1 h-2 rounded bg-muted overflow-hidden">
            <div
              className={row.id === 'ready' ? 'h-full bg-blue-500' : 'h-full bg-emerald-500'}
              style={{ width: `${Math.min(100, (row.durationMs / scaleMs) * 100)}%` }}
            />
          </div>
          <span className="text-xs font-mono w-14 shrink-0 text-right text-muted-foreground">
            {formatDurationLabel(row.durationMs)}
          </span>
        </div>
      ))}
    </div>
  );
}

ProvisionTimingSummaryBars.displayName = 'ProvisionTimingSummaryBars';
